// components/maps/HospitalMarker.jsx
import React from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Star, Navigation, Phone } from 'lucide-react';

// Custom Icon Creator
const createCustomIcon = (color, isEmergency = false) => {
  const svgIcon = `
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(20, 35)">
        <path d="M0,-30 C-8,-30 -15,-24 -15,-15 C-15,-7 -8,0 0,0 C8,0 15,-7 15,-15 C15,-24 8,-30 0,-30" 
              fill="${color}" stroke="white" stroke-width="2"/>
        ${isEmergency ? 
          `<text x="0" y="-12" text-anchor="middle" fill="white" font-size="16" font-weight="bold">!</text>` : 
          `<circle cx="0" cy="-15" r="3" fill="white"/>`
        }
      </g>
    </svg>
  `;

  return L.divIcon({
    html: svgIcon,
    iconSize: [40, 40],
    iconAnchor: [20, 35],
    popupAnchor: [0, -35],
    className: 'custom-hospital-marker'
  });
};

const HospitalMarker = ({ hospital, isSelected, onClick }) => {
  // Validate coordinates
  if (!hospital.location?.coordinates) {
    console.warn('Hospital missing coordinates:', hospital.name);
    return null;
  }

  const [lng, lat] = hospital.location.coordinates;
  
  // Validate coordinate values
  if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
    console.warn('Invalid coordinates for hospital:', hospital.name, { lat, lng });
    return null;
  }

  // Determine marker color
  const isEmergency = hospital.hasEmergency;
  const color = isSelected ? '#DC2626' : isEmergency ? '#059669' : '#3B82F6';

  // Handle navigation
  const handleNavigate = () => {
    if (hospital.location?.coordinates) {
      const [lng, lat] = hospital.location.coordinates;
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
    }
  };

  // Handle call
  const handleCall = () => {
    if (hospital.primaryPhone) {
      window.location.href = `tel:${hospital.primaryPhone}`;
    }
  };

  return (
    <Marker
      position={[lat, lng]}
      icon={createCustomIcon(color, isEmergency)}
      eventHandlers={{
        click: () => onClick(hospital)
      }}
    >
      <Popup>
        <div className="min-w-[200px]">
          <h4 className="font-semibold mb-1">{hospital.name}</h4>
          
          {hospital.rating?.average > 0 && (
            <div className="flex items-center gap-1 mb-2">
              <Star size={14} className="text-yellow-500 fill-current" />
              <span className="text-sm">{hospital.rating.average}</span>
              <span className="text-xs text-gray-500">
                ({hospital.rating.totalReviews || 0})
              </span>
            </div>
          )}
          
          {hospital.currentStatus && (
            <span className={`inline-block text-xs px-2 py-1 rounded-full mb-2 ${
              hospital.currentStatus.status === 'open' 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              {hospital.currentStatus.text}
            </span>
          )}
          
          <p className="text-xs text-gray-600 mb-2">
            {hospital.location.address}
          </p>
          
          {hospital.distance !== undefined && (
            <p className="text-xs text-primary-600 font-medium mb-2">
              {hospital.distance} km away
            </p>
          )}
          
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleNavigate}
              className="flex-1 flex items-center justify-center gap-1 text-xs px-2 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
            >
              <Navigation size={12} />
              Directions
            </button>
            {hospital.primaryPhone && (
              <button
                onClick={handleCall}
                className="flex-1 flex items-center justify-center gap-1 text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
              >
                <Phone size={12} />
                Call
              </button>
            )}
          </div>
          
          <a
            href={`/hospitals/${hospital._id}`}
            className="block text-center text-xs text-primary-600 hover:text-primary-700 mt-2 font-medium"
          >
            View Details →
          </a>
        </div>
      </Popup>
    </Marker>
  );
};

export default HospitalMarker;